/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from 'react'
import { Eye, EyeOff, ArrowRight, Lock, Mail } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../Contexts/AuthContext'
import { motion, AnimatePresence } from 'framer-motion';
import { log, error as logError } from "../utils/logger";
import { API_BASE_URL } from './TokenChecker';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false)
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [remember, setRemember] = useState(true)
  const { login } = useAuth();
  const queryParams = new URLSearchParams(location.search);
  const nextPage = queryParams.get('next');

  const ErrorMessage = ({ message }) => (
    <AnimatePresence>
      {message && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="bg-red-50 border-l-4 border-red-400 text-red-700 px-4 py-3 rounded-md mb-4 shadow-sm text-sm font-medium"
        >
          {message}
        </motion.div>
      )}
    </AnimatePresence>
  );

  const isValidGmail = (value) => {
    const normalized = (value || "").trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) return false;
    return normalized.endsWith("@gmail.com");
  };

  const handleSubmit = async (e) =>{
    e.preventDefault();
    log("Starting backend connection")
    const normalizedEmail = (email || "").trim().toLowerCase();
    if (!isValidGmail(normalizedEmail)) {
      setError("Please use a valid Gmail address (e.g., name@gmail.com).");
      log("Invalid email");
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/login/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          },
          body: JSON.stringify({email: normalizedEmail, password})

      });
      const data = await response.json();

      if(response.ok){
        log("Login Successful", nextPage);
        const {first_name, last_name} = data.user;
        
        login(normalizedEmail, data.tokens.access, data.tokens.refresh, first_name, last_name, remember);

        log("Login successful, redirecting to next page:", nextPage);
        if(nextPage){
          navigate(nextPage)
        }else{
          navigate('/home');
        }

      } else{
        setError(data.error)
        log(data.error)
      }

    } catch (err){
      setError('Something went wrong. Please try again later.');
      logError(err)
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-x-hidden bg-gradient-to-b from-[#F5F7FA] to-[#E4E7EC] text-[#2A2D34] px-4 py-12">
      <div className="pointer-events-none fixed -top-24 -right-28 -z-10 h-80 w-80 rounded-full bg-[#10A7F3]/20 blur-3xl" />
      <div className="pointer-events-none fixed -bottom-32 -left-24 -z-10 h-96 w-96 rounded-full bg-[#10F3D6]/20 blur-3xl" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-md"
      >
        <div className="mb-6 flex items-center justify-center cursor-pointer select-none" onClick={() => navigate('/') }>
          <span className="text-4xl font-extrabold tracking-tight bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] bg-clip-text text-transparent">Flowman</span>
        </div>

        <div className="relative overflow-hidden rounded-2xl border border-white/60 bg-white/80 p-6 shadow-xl backdrop-blur">
          <div className="absolute inset-0 -z-10 rounded-3xl bg-gradient-to-br from-[#10A7F3]/10 to-[#10F3D6]/20 blur-2xl" />
          <div className="mb-4 flex items-center gap-2">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#10A7F3]/30 bg-white/80 px-3 py-1 text-sm font-medium text-[#0D8ED3] shadow-sm">
              <span className="inline-block h-2 w-2 rounded-full bg-[#10A7F3]" />
              Welcome back
            </div>
          </div>

          <h2 className="text-2xl font-bold">Sign in to your account</h2>
          <p className="mt-1 text-sm text-[#2A2D34]/70">Your mission control for productivity</p>

          <div className="mt-6">
            <ErrorMessage message={error} />
            <form className="space-y-5" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="mb-1 block text-sm font-medium text-[#2A2D34]">Email address</label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#10A7F3]">
                    <Mail className="h-4 w-4" />
                  </span>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="block w-full rounded-md border border-[#10A7F3]/20 bg-white/70 py-2 pl-9 pr-3 text-sm shadow-sm outline-none placeholder-[#2A2D34]/50 focus:border-[#10A7F3] focus:ring-2 focus:ring-[#10A7F3]/30"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="mb-1 block text-sm font-medium text-[#2A2D34]">Password</label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#10A7F3]">
                    <Lock className="h-4 w-4" />
                  </span>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e)=>setPassword(e.target.value)}
                    required
                    className="block w-full rounded-md border border-[#10A7F3]/20 bg-white/70 py-2 pl-9 pr-9 text-sm shadow-sm outline-none placeholder-[#2A2D34]/50 focus:border-[#10A7F3] focus:ring-2 focus:ring-[#10A7F3]/30"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-[#2A2D34]/60 hover:text-[#2A2D34]"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    checked={remember}
                    onChange={(e)=> setRemember(e.target.checked)}
                    className="h-4 w-4 rounded border-[#10A7F3]/30 text-[#10A7F3] focus:ring-[#10A7F3]"
                  />
                  <span>Remember me</span>
                </label>

                <a href="/forgot-password" className="text-sm font-medium text-[#0D8ED3] hover:text-[#10A7F3]">
                  Forgot your password?
                </a>
              </div>

              <button
                type="submit"
                className="group relative inline-flex w-full items-center justify-center gap-2 rounded-md bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] px-4 py-2 text-sm font-semibold text-white shadow hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#10A7F3]"
              >
                Sign in
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-[#2A2D34]/80">
              Don&apos;t have an account?{' '}
              <button className="font-semibold text-[#0D8ED3] hover:text-[#10A7F3]" onClick={()=>navigate('/signup')}>
                Sign up
              </button>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default LoginPage
